// Circular dock-tab geometry for the production firefly affordance (V10+).
// The hit target is a circle whose edge touches the dock anchor point; the
// firefly rests at the circle center and highlight travel stays inside the hit
// area, biased toward the viewport interior.

import type { DockEdge } from "../activityDockPositionMath";

/** Max glow half (12) + highlight nudge (32) + hover (6) + jitter (5) + pad. */
export const FIREFLY_DOCK_HIT_RADIUS_PX = 56;

export const FIREFLY_DOCK_HIT_DIAMETER_PX = FIREFLY_DOCK_HIT_RADIUS_PX * 2;

/** Golden halo rings — sized to the bug glow, not the full click target. */
export const FIREFLY_HALO_DIAMETER_PX = 36;

const HIGHLIGHT_OFFSET_PX = 32;
const HIGHLIGHT_INSET_PX = 6;

export type FireflyCircleTabLayout = {
  diameterPx: number;
  radiusPx: number;
  /** Circle point on the dock-attached edge (click target boundary). */
  dockEdgeX: number;
  dockEdgeY: number;
  /** Firefly rest + travel center (middle of click area). */
  centerX: number;
  centerY: number;
};

function clampPointToCircle(
  centerX: number,
  centerY: number,
  x: number,
  y: number,
  maxRadiusPx: number,
): { x: number; y: number } {
  const dx = x - centerX;
  const dy = y - centerY;
  const dist = Math.hypot(dx, dy);
  if (dist <= maxRadiusPx || dist === 0) {
    return { x, y };
  }
  const scale = maxRadiusPx / dist;
  return { x: centerX + dx * scale, y: centerY + dy * scale };
}

/** Circle inscribed in a square hit box; one boundary point touches the dock. */
export function fireflyCircleTabLayout(edge: DockEdge): FireflyCircleTabLayout {
  const radiusPx = FIREFLY_DOCK_HIT_RADIUS_PX;
  const diameterPx = FIREFLY_DOCK_HIT_DIAMETER_PX;
  const centerX = radiusPx;
  const centerY = radiusPx;

  switch (edge) {
    case "right":
      return {
        diameterPx,
        radiusPx,
        dockEdgeX: diameterPx,
        dockEdgeY: centerY,
        centerX,
        centerY,
      };
    case "left":
      return {
        diameterPx,
        radiusPx,
        dockEdgeX: 0,
        dockEdgeY: centerY,
        centerX,
        centerY,
      };
    case "top":
      return {
        diameterPx,
        radiusPx,
        dockEdgeX: centerX,
        dockEdgeY: 0,
        centerX,
        centerY,
      };
    case "bottom":
      return {
        diameterPx,
        radiusPx,
        dockEdgeX: centerX,
        dockEdgeY: diameterPx,
        centerX,
        centerY,
      };
  }
}

/** Rest position — center of the circular click area. */
export function fireflyAnchorInTab(
  edge: DockEdge,
  _tabWidthPx?: number,
  _tabHeightPx?: number,
): { x: number; y: number } {
  const { centerX, centerY } = fireflyCircleTabLayout(edge);
  return { x: centerX, y: centerY };
}

/** Highlight point inside the click circle, nudged toward the viewport interior. */
export function fireflyHighlightInTab(
  edge: DockEdge,
  _tabWidthPx?: number,
  _tabHeightPx?: number,
  offsetPx: number = HIGHLIGHT_OFFSET_PX,
): { x: number; y: number } {
  const { centerX, centerY, radiusPx } = fireflyCircleTabLayout(edge);
  const maxRadiusPx = Math.max(8, radiusPx - HIGHLIGHT_INSET_PX);
  const nudge = Math.min(offsetPx, maxRadiusPx);

  let x = centerX;
  let y = centerY;
  switch (edge) {
    case "right":
      x = centerX - nudge;
      break;
    case "left":
      x = centerX + nudge;
      break;
    case "top":
      y = centerY + nudge;
      break;
    case "bottom":
      y = centerY - nudge;
      break;
  }

  return clampPointToCircle(centerX, centerY, x, y, maxRadiusPx);
}
